import { ROOT_SHELL_HOST_MAX_LENGTH } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/served-hosts.schemas.ts
/**
* The served-host document, `hosts.json` (layered shell surfaces, R-12 d, R-25 c and d).
*
* An org admin assigns a root shell to a hostname in `auth.root_shell_assignments` (WS-5). The
* operator program plans those rows (WS-17) and writes one JSON document under the key
* {@link SERVED_HOSTS_DOCUMENT_KEY} of a ConfigMap in the release namespace. The document lists
* every accepted host, a host that still waits for its certificate included, with the org that
* assigned it. The DP chart mounts that ConfigMap into core-api and sets `ROOT_SHELL_HOSTS_PATH`
* (WS-16). core-api reads the file and binds each listed host to its org, and it binds no host the
* document does not list (WS-18).
*
* This file is the one home of that document. The writer calls {@link renderServedHostsDocument}
* and the reader calls {@link parseServedHostsDocument}, so the two cannot disagree on the bytes.
* Neither side declares the shape again.
*
* Every object is strict at both levels, so a key this file does not name refuses the whole
* document. One bad entry also refuses the whole document: the reader keeps its last good
* bindings in place of a partial read that could drop a live binding (WS-18 §3.1).
*
* No `.transform()` appears here, so every schema stays representable for
* `createOpenApiDocument()`. The refines below are dropped from a JSON Schema export and still run
* on every parse.
*
* This module does no I/O and holds no state.
*/
/**
* The ConfigMap data key the operator program writes and the file name the chart mounts:
* `hosts.json`. The chart's mount, the operator's ConfigMap write and core-api's
* `ROOT_SHELL_HOSTS_PATH` all name this one key.
*/
const SERVED_HOSTS_DOCUMENT_KEY = "hosts.json";
/** The one document version this contract reads and writes. A document with any other `version` is refused. */
const SERVED_HOSTS_DOCUMENT_VERSION = 1;
/**
* The longest document text, in UTF-16 code units: 1 MiB, the data limit of one ConfigMap.
* {@link parseServedHostsDocument} refuses a longer text before it parses it, and
* {@link renderServedHostsDocument} throws before it returns one.
*/
const SERVED_HOSTS_DOCUMENT_MAX_LENGTH = 1048576;
/** The longest org slug an entry carries, in characters. The same bound as `ShellSyncRequestSchema.orgSlug`. */
const SERVED_HOST_ORG_SLUG_MAX_LENGTH = 63;
/** The shortest org slug an entry carries, in characters. The same bound as `ShellSyncRequestSchema.orgSlug`. */
const SERVED_HOST_ORG_SLUG_MIN_LENGTH = 2;
/** The message a host outside the hostname grammar earns. */
const SERVED_HOST_MESSAGE = "host must be a lowercase DNS name of two or more labels, with no port, no trailing dot and no IP literal";
/** The message an entry earns when its `origin` is not `https://` followed by its `host`. */
const SERVED_HOST_ORIGIN_MESSAGE = "origin must be https:// followed by host";
/** The message the second and each later entry for one host earns. */
const SERVED_HOST_DUPLICATE_MESSAGE = "host must appear at most once";
/**
* The hostname grammar: two or more labels of `[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?` joined by dots,
* and a last label that is not all digits. It is the `grammar` arm of `refusalFor` in
* `@zion/sdk-contracts` after the length test, which the schema runs first. The test suite holds the
* schema and `refusalFor` to one verdict. A port, an upper-case letter, a trailing dot, a `*`, an
* IPv4 literal and an IPv6 literal all fail it. The admin contract `ROOT_SHELL_HOST_PATTERN` in
* `@zion/sdk-dp-admin-contracts` holds the same text; this package does not depend on that one.
*/
const SERVED_HOST_PATTERN = /* @__PURE__ */ new RegExp("^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?![0-9]+$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$");
/**
* One host and the org that assigned it, as the operator program knows it before it renders the
* document: the input row of {@link renderServedHostsDocument}.
*
* - `host`: at most `ROOT_SHELL_HOST_MAX_LENGTH` (253) characters, in the hostname grammar
*   ({@link SERVED_HOST_MESSAGE}). The value is already normalised: lower case, no port. So a
*   reader compares it with a normalised `Host` header byte for byte.
* - `organizationId`: the DP-local `auth.organizations.id`, an RFC 9562 UUID.
* - `orgSlug`: that org's slug, {@link SERVED_HOST_ORG_SLUG_MIN_LENGTH} to
*   {@link SERVED_HOST_ORG_SLUG_MAX_LENGTH} characters.
*
* Strict: an unknown key is refused.
*/
const ServedHostBindingSchema = z.strictObject({
	host: z.string().max(ROOT_SHELL_HOST_MAX_LENGTH, { message: SERVED_HOST_MESSAGE }).regex(SERVED_HOST_PATTERN, { message: SERVED_HOST_MESSAGE }),
	organizationId: z.uuid(),
	orgSlug: z.string().min(2).max(63)
});
/**
* One entry of `hosts`: a {@link ServedHostBinding} plus `origin`, which must equal `https://`
* followed by `host`. A mismatched `origin` is refused with {@link SERVED_HOST_ORIGIN_MESSAGE} at
* the issue path `['origin']`. Strict: an unknown key is refused.
*/
const ServedHostEntrySchema = ServedHostBindingSchema.extend({ origin: z.string() }).refine((entry) => entry.origin === `https://${entry.host}`, {
	message: SERVED_HOST_ORIGIN_MESSAGE,
	path: ["origin"]
}).readonly();
/**
* The whole `hosts.json` document: `{ version: 1, hosts: [...] }`.
*
* - `version` is the literal {@link SERVED_HOSTS_DOCUMENT_VERSION}; any other value is refused.
* - `hosts` holds every accepted host once. An empty array is valid and binds nothing. A host that
*   appears a second time is refused with {@link SERVED_HOST_DUPLICATE_MESSAGE} at
*   `['hosts', <index>, 'host']`, one issue for each later copy.
* - The schema carries no host count cap. `MAX_ROOT_SHELL_HOSTS_PER_ORG` and the operator's DP cap
*   bound the writer (WS-17); a reader that refused on a count would drop every live binding.
*
* Strict at both levels. The parsed value is frozen: the document, `hosts` and every entry.
*/
const ServedHostsDocumentSchema = z.strictObject({
	version: z.literal(1),
	hosts: z.array(ServedHostEntrySchema).superRefine((hosts, ctx) => {
		const seen = /* @__PURE__ */ new Set();
		hosts.forEach((entry, index) => {
			if (seen.has(entry.host)) ctx.addIssue({
				code: "custom",
				message: SERVED_HOST_DUPLICATE_MESSAGE,
				path: [index, "host"]
			});
			seen.add(entry.host);
		});
	}).readonly()
}).readonly();
/**
* The document that binds no host, `{"version":1,"hosts":[]}`. The operator program writes it when it
* serves no host; {@link renderServedHostsDocument} of an empty list answers its text. Frozen at
* every depth.
*/
const EMPTY_SERVED_HOSTS_DOCUMENT = Object.freeze({
	version: 1,
	hosts: Object.freeze([])
});
/**
* Parses the text of a `hosts.json` file. core-api's served-host reader and its Fake call it
* (WS-18).
*
* - A text longer than {@link SERVED_HOSTS_DOCUMENT_MAX_LENGTH}: `{ ok: false, reason: 'too-large' }`.
* - A text `JSON.parse` refuses: `{ ok: false, reason: 'invalid-json' }`.
* - JSON with a `__proto__` key at any depth, or JSON that {@link ServedHostsDocumentSchema}
*   refuses: `{ ok: false, reason: 'invalid-document' }`. The `__proto__` check runs in the parser
*   because zod 4's object parser drops that key and accepts the rest.
* - Otherwise: `{ ok: true, document }`, with `document` frozen at every depth.
*
* A refusal carries the reason code and nothing from the text, so a caller can log it without the
* file body. Pure; never throws.
*/
function parseServedHostsDocument(text) {
	if (text.length > 1048576) return {
		ok: false,
		reason: "too-large"
	};
	let json;
	let protoKey = false;
	try {
		json = JSON.parse(text, (key, value) => {
			if (key === "__proto__") protoKey = true;
			return value;
		});
	} catch {
		return {
			ok: false,
			reason: "invalid-json"
		};
	}
	if (protoKey) return {
		ok: false,
		reason: "invalid-document"
	};
	const result = ServedHostsDocumentSchema.safeParse(json);
	return result.success ? {
		ok: true,
		document: result.data
	} : {
		ok: false,
		reason: "invalid-document"
	};
}
/**
* Renders the `hosts.json` text for a list of accepted hosts. The operator program calls it and
* writes the answer under {@link SERVED_HOSTS_DOCUMENT_KEY} (WS-17).
*
* - Each row gives `host`, `organizationId` and `orgSlug`; any other property of a row is ignored,
*   so a caller can pass its own planned-host rows.
* - The entries are sorted by `host` in code-unit order, and each gains `origin` `https://<host>`.
*   So the same set of rows answers the same bytes in any input order.
* - The answer is `JSON.stringify` of `{ version: 1, hosts }` with keys in the order `version`,
*   `hosts`, and per entry `host`, `organizationId`, `orgSlug`, `origin`. No row answers
*   `{"version":1,"hosts":[]}`.
*
* Errors: a row outside {@link ServedHostBindingSchema}, or a host in two rows, throws the
* `ZodError` of {@link ServedHostsDocumentSchema}. An answer longer than
* {@link SERVED_HOSTS_DOCUMENT_MAX_LENGTH} throws a `RangeError`. So the writer never writes a
* document the reader refuses. Pure; it does not change `bindings`.
*/
function renderServedHostsDocument(bindings) {
	const hosts = bindings.map(({ host, organizationId, orgSlug }) => ({
		host,
		organizationId,
		orgSlug,
		origin: `https://${host}`
	})).sort((a, b) => a.host < b.host ? -1 : a.host > b.host ? 1 : 0);
	const document = ServedHostsDocumentSchema.parse({
		version: 1,
		hosts
	});
	const text = JSON.stringify(document);
	if (text.length > 1048576) throw new RangeError(`hosts.json would be ${text.length} characters; the limit is ${SERVED_HOSTS_DOCUMENT_MAX_LENGTH}`);
	return text;
}
//#endregion
export { EMPTY_SERVED_HOSTS_DOCUMENT, SERVED_HOSTS_DOCUMENT_KEY, SERVED_HOSTS_DOCUMENT_MAX_LENGTH, SERVED_HOSTS_DOCUMENT_VERSION, SERVED_HOST_DUPLICATE_MESSAGE, SERVED_HOST_MESSAGE, SERVED_HOST_ORG_SLUG_MAX_LENGTH, SERVED_HOST_ORG_SLUG_MIN_LENGTH, SERVED_HOST_ORIGIN_MESSAGE, SERVED_HOST_PATTERN, ServedHostBindingSchema, ServedHostEntrySchema, ServedHostsDocumentSchema, parseServedHostsDocument, renderServedHostsDocument };

//# sourceMappingURL=served-hosts.schemas.js.map